/**
 * 纵横杯 16 环节「光学轨道」详情（教练台）。
 *
 * 只做「查看」：点击环节展示赛制信息，不直接跳转（仍由控制条推进）。
 * 视觉：单行轨道 + 当前段发光展开，取代旧版 16 个彩色方框。
 */
import { useMemo, useState } from 'react';
import { STAGE_SCHEDULE, sideLabel, type StageDefinition } from '@zongheng/core-schema';
import { useArena } from '../../state/ArenaContext.tsx';
import { Badge, cx } from '../ui/primitives.tsx';

const KIND_LABEL: Record<StageDefinition['kind'], string> = {
  CONSTRUCTIVE: '申论',
  CROSS_EXAM: '单向质询',
  DUEL: '1v1 对辩',
  FINAL_REBUTTAL: '结辩',
};

const CARD_SLOT_LABEL: Record<NonNullable<StageDefinition['cardSlot']>, string> = {
  CONSTRUCTIVE: '申论策略卡',
  CROSS_EXAM: '质询任务卡',
  SANDBOX: '沙盘大纲',
};

function StageInfoStrip({ stage }: { stage: StageDefinition }) {
  return (
    <div
      className="lens-inset mt-3 grid gap-x-6 gap-y-2 px-4 py-3 text-[11px] text-slate-300 sm:grid-cols-3 lg:grid-cols-6"
      data-testid="stage-info"
    >
      <div>
        <div className="micro-label">STAGE</div>
        <div className="mt-0.5 flex items-center gap-1.5 text-slate-100">
          <span
            className="h-1 w-1 rounded-full"
            style={{
              background: stage.speakerSide === 'AFF' ? '#7DD3FC' : '#FDA4AF',
              boxShadow: `0 0 8px ${stage.speakerSide === 'AFF' ? '#7DD3FC' : '#FDA4AF'}`,
            }}
          />
          环节 {stage.index} · {sideLabel(stage.speakerSide)}
        </div>
      </div>
      <div>
        <div className="micro-label">环节名称</div>
        <div className="mt-0.5 text-slate-100">{stage.name}</div>
      </div>
      <div>
        <div className="micro-label">计时模式</div>
        <div className="mt-0.5">{stage.clockMode === 'CHESS' ? '双表 Chess Clock' : '单向计时'}</div>
      </div>
      <div>
        <div className="micro-label">双方预算</div>
        <div className="mono mt-0.5">
          AFF {stage.budgets.AFF}s / NEG {stage.budgets.NEG}s
        </div>
      </div>
      <div>
        <div className="micro-label">读卡期 / 发牌</div>
        <div className="mt-0.5">
          {stage.prepSec > 0 ? `${stage.prepSec}s 读卡` : '无读卡'} ·{' '}
          {stage.cardSlot ? CARD_SLOT_LABEL[stage.cardSlot] : '无发牌'}
        </div>
      </div>
      <div>
        <div className="micro-label">双盲配对</div>
        <div className="mono mt-0.5">{stage.pairId ?? '—'}</div>
      </div>
    </div>
  );
}

export function StageTimeline() {
  const { state } = useArena();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const completed = useMemo(() => new Set(state.completedStages), [state.completedStages]);
  const selectedStage =
    selectedIndex === null
      ? state.stage
      : STAGE_SCHEDULE.find((stage) => stage.index === selectedIndex) ?? state.stage;

  return (
    <section className="lens p-4">
      <header className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="status-dot status-dot--live" />
            <h2 className="text-[13px] font-medium tracking-wide text-slate-100">16 环节光学轨道</h2>
          </div>
          <p className="micro-label mt-1">
            {completed.size} / {STAGE_SCHEDULE.length} COMPLETED · 点击查看赛制信息（不跳转）
          </p>
        </div>
        <span className="micro-label hidden sm:block">改良奥瑞冈制 · 申论 / 质询 / 对辩 / 结辩</span>
      </header>

      <div className="mt-3 overflow-x-auto pb-1">
        <ol className="flex min-w-max items-stretch gap-1" data-testid="coach-stage-timeline">
          {STAGE_SCHEDULE.map((stage) => {
            const isCurrent = state.stageIndex === stage.index;
            const isCompleted = completed.has(stage.index);
            const isSelected = selectedIndex === stage.index;
            const accent = stage.speakerSide === 'AFF' ? '#7DD3FC' : '#FDA4AF';
            return (
              <li key={stage.index} className="flex">
                <button
                  type="button"
                  data-testid={`stage-chip-${stage.index}`}
                  onClick={() => setSelectedIndex(stage.index)}
                  title={`${stage.name} · ${KIND_LABEL[stage.kind]}`}
                  className={cx(
                    'group relative flex flex-col justify-between overflow-hidden rounded-md px-2 py-1.5 text-left transition-all',
                    isCurrent ? 'min-w-[104px] bg-white/[0.08]' : 'w-[54px] bg-white/[0.02] hover:bg-white/[0.05]',
                    isSelected && !isCurrent && 'bg-white/[0.06]',
                    isCurrent &&
                      'shadow-[inset_0_1px_0_0_rgba(255,255,255,0.22),inset_0_0_0_1px_rgba(255,255,255,0.1)]',
                  )}
                >
                  {isCurrent && (
                    <span
                      className="absolute inset-y-0 left-0 w-px"
                      style={{ background: accent, boxShadow: `0 0 10px ${accent}` }}
                    />
                  )}
                  <div className="flex items-center justify-between gap-1">
                    <span className="mono text-[9px] text-slate-500">{String(stage.index).padStart(2, '0')}</span>
                    <span
                      className={cx(
                        'h-1 w-1 rounded-full',
                        isCompleted
                          ? 'bg-white/70 shadow-[0_0_6px_rgba(255,255,255,0.6)]'
                          : isCurrent
                            ? 'bg-white shadow-[0_0_10px_rgba(255,255,255,0.9)]'
                            : 'bg-white/15',
                      )}
                    />
                  </div>
                  <div
                    className={cx(
                      'mt-1 whitespace-nowrap text-[10px]',
                      isCurrent ? 'font-medium text-slate-100' : 'text-slate-400',
                    )}
                  >
                    {isCurrent ? stage.shortName : isCompleted ? '✓' : KIND_LABEL[stage.kind].slice(0, 1)}
                  </div>
                  {isCurrent && (
                    <div className="mt-1 h-[2px] w-full overflow-hidden rounded-full bg-white/10">
                      <div
                        className="h-full rounded-full bg-white/80 shadow-[0_0_6px_rgba(255,255,255,0.7)]"
                        style={{
                          width: `${
                            stage.budgets.AFF + stage.budgets.NEG > 0
                              ? Math.max(
                                  0,
                                  Math.min(
                                    100,
                                    (1 -
                                      (state.remainingMs.AFF + state.remainingMs.NEG) /
                                        ((stage.budgets.AFF + stage.budgets.NEG) * 1000)) *
                                      100,
                                  ),
                                )
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                  )}
                </button>
              </li>
            );
          })}
        </ol>
      </div>

      {selectedStage && <StageInfoStrip stage={selectedStage} />}
      <p className="mt-2 text-[10px] text-slate-600">
        <Badge tone="neutral">只读</Badge>{' '}
        点击轨道仅查看信息，不跳转；实际流转请使用上方控制条的「下一环节 / 结束本环」。
      </p>
    </section>
  );
}
